import React, { useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Toast from "../components/Toast";
import { offerService, orderService } from "../services/apiService";

// Fixed (non-editable) message template. The shopkeeper only fills Title and
// dates; placeholders are substituted per-recipient by the backend.
// {customNote} is an optional line the user can fill; when blank it collapses
// so no empty gap remains.
const MESSAGE_TEMPLATE =
  "Hi {customerName} 👋\n\n" +
  "🎉 {offerTitle}\n\n" +
  "{itemsBlock}" +
  "📅 Offer Period: {offerStartDate} to {validTill}\n\n" +
  "Don't miss out! Visit us during the offer period.\n\n" +
  "{customNote}" +
  "Thank you for shopping with us!\n\n" +
  "*{shopName}*";

const PAGE_SIZE = 15;   // customers shown per page (server-side)
const SEND_BATCH = 200; // recipients per backend send call
// wa.me opens one browser tab per recipient (manual tap). Opening more than
// this at once is impractical and browsers block it — so we cap the free
// channel and tell the user. Larger blasts need the paid API (later).
const WA_ME_OPEN_LIMIT = 20;

const OfferCreate = () => {
  // Offer fields
  const [title, setTitle] = useState("");
  const [validFrom, setValidFrom] = useState("");
  const [validTo, setValidTo] = useState("");
  const [customNote, setCustomNote] = useState("");
  const [errors, setErrors] = useState({});

  // Offer item picker (product + size with capped discount)
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [removeModalOpen, setRemoveModalOpen] = useState(false); // "remove items" popup
  const [itemsCatalog, setItemsCatalog] = useState([]);   // from backend
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsSearched, setItemsSearched] = useState(false); // has a search run yet?
  // Server-side filters (applied on explicit Search, not per keystroke).
  const [fName, setFName] = useState("");
  const [fType, setFType] = useState("");
  const [fManufacturer, setFManufacturer] = useState("");
  // Distinct filter options (facets), populated from the backend and narrowed
  // by the current selections so the pickers stay mutually consistent.
  const [optNames, setOptNames] = useState([]);
  const [optTypes, setOptTypes] = useState([]);
  const [optManufacturers, setOptManufacturers] = useState([]);
  // Selected items keyed by "name|size" -> { productName, size, maxDiscount, discount }
  const [selectedItems, setSelectedItems] = useState(() => new Map());

  // Audience — server-side paginated
  const [pageCustomers, setPageCustomers] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  // Full (unfiltered) audience size — the denominator for "X/total selected".
  const [audienceTotal, setAudienceTotal] = useState(0);
  const [custLoading, setCustLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [filterMobile, setFilterMobile] = useState(""); // exact-match server filter

  // Selection is a RULE, not a big list: either "all matching" minus exclusions,
  // or "none" plus an explicit included set. This scales to any customer count.
  const [selectAll, setSelectAll] = useState(false);
  const [excluded, setExcluded] = useState(() => new Set()); // mobiles, when selectAll = true
  // Explicitly-picked customers, keyed by mobile -> name, when selectAll = false.
  // Storing the name means multi-select keeps each customer's real name.
  const [included, setIncluded] = useState(() => new Map());

  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState(null);
  const [channel, setChannel] = useState("wa_me"); // this client's offer channel
  const [waConnected, setWaConnected] = useState(true); // WhatsApp API connection status
  const [toast, setToast] = useState(null);

  // Load the client's configured offer channel (wa_me / sms / whatsapp_api).
  useEffect(() => {
    let cancelled = false;
    offerService
      .getOfferChannel()
      .then((res) => { if (!cancelled && res?.value) setChannel(res.value); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // When the channel is WhatsApp API, check whether this client has connected
  // their WABA (and set a template) so we can guide them if not.
  useEffect(() => {
    let cancelled = false;
    if (channel !== "whatsapp_api") { setWaConnected(true); return; }
    offerService
      .getWhatsAppConfig()
      .then((cfg) => {
        if (!cancelled) {
          setWaConnected(Boolean(cfg?.connected && cfg?.templateName));
        }
      })
      .catch(() => { if (!cancelled) setWaConnected(false); });
    return () => { cancelled = true; };
  }, [channel]);

  const showMsg = (type, text) => setToast({ type, message: text });

  // Fetch one page of customers from the backend (true server-side pagination).
  const fetchPage = useCallback(async (pageNo, mobile) => {
    setCustLoading(true);
    try {
      const data = await orderService.searchCustomers({
        mobileNumber: mobile ? mobile.trim() : null,
        page: pageNo,
        size: PAGE_SIZE,
      });
      const total = data.totalElements ?? (data.content ? data.content.length : 0);
      setPageCustomers(data.content || []);
      setPage(data.page ?? pageNo);
      setTotalPages(data.totalPages ?? 0);
      setTotalElements(total);
      // Capture the full audience size only when there is no mobile filter.
      if (!mobile || !mobile.trim()) {
        setAudienceTotal(total);
      }
      setLoaded(true);
    } catch {
      setPageCustomers([]);
      showMsg("error", "Failed to load customers");
    } finally {
      setCustLoading(false);
    }
  }, []);

  const loadCustomers = () => {
    // Loading/refreshing the audience resets the selection rule.
    setSelectAll(false);
    setExcluded(new Set());
    setIncluded(new Map());
    fetchPage(0, filterMobile);
  };

  // Live prefix filter: once customers are loaded, re-query (debounced) as the
  // mobile filter changes. IMPORTANT: the selection rule (selectAll + excluded
  // / included, keyed by mobile) is PRESERVED across filtering — so you can
  // Select All, filter to one customer, uncheck them, clear the filter, and the
  // exclusion still holds (e.g. 99/100).
  useEffect(() => {
    if (!loaded) return;
    const t = setTimeout(() => {
      fetchPage(0, filterMobile);
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMobile]);

  // Is a given customer currently selected under the active rule?
  const isSelected = useCallback(
    (mobile) => (selectAll ? !excluded.has(mobile) : included.has(mobile)),
    [selectAll, excluded, included],
  );

  const toggleOne = (customer) => {
    const mobile = customer.mobileNumber;
    if (selectAll) {
      setExcluded((prev) => {
        const next = new Set(prev);
        next.has(mobile) ? next.delete(mobile) : next.add(mobile);
        return next;
      });
    } else {
      setIncluded((prev) => {
        const next = new Map(prev);
        next.has(mobile) ? next.delete(mobile) : next.set(mobile, customer.customerName || "");
        return next;
      });
    }
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      // Turn off: select none.
      setSelectAll(false);
      setExcluded(new Set());
      setIncluded(new Map());
    } else {
      // Turn on: select everyone matching the current filter.
      setSelectAll(true);
      setExcluded(new Set());
      setIncluded(new Map());
    }
  };

  // Count of who will actually receive the offer (against the FULL audience).
  const selectedCount = selectAll
    ? Math.max(0, audienceTotal - excluded.size)
    : included.size;

  // Today's date as YYYY-MM-DD (local), used as the min for Valid Till.
  const todayStr = new Date().toLocaleDateString("en-CA");

  // Format an ISO date (YYYY-MM-DD) as dd-MM-yyyy for the preview.
  const fmtDate = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${d}-${m}-${y}`;
  };

  // Live, read-only preview. The customer name is a NEUTRAL placeholder — the
  // backend substitutes each recipient's own name (or mobile) at send time, so
  // the preview must not pin to any single selected customer.
  // Format a size for display (drop trailing .00).
  const fmtSize = (s) => {
    if (s === null || s === undefined || s === "") return "";
    const n = Number(s);
    return Number.isNaN(n) ? String(s) : (Number.isInteger(n) ? String(n) : String(n));
  };

  // A size counts only when it's a real, non-zero value. Zero / null / blank
  // means the product has no size dimension, so we omit "Size X" entirely.
  const hasSize = (s) => {
    if (s === null || s === undefined || String(s).trim() === "") return false;
    const n = Number(s);
    return !Number.isNaN(n) && n !== 0;
  };

  // Build the "🏷️ Offer Items" block from selected items, grouped by
  // manufacturer: the manufacturer name is a heading, its items listed under
  // it. Items with no manufacturer fall under an "Other" group (only shown if
  // such items exist). Groups and items keep the order they were added in.
  const buildItemsBlock = () => {
    const rows = Array.from(selectedItems.values());
    if (rows.length === 0) return "";

    // Preserve insertion order of both groups and their items.
    const groups = new Map(); // manufacturer -> [lines]
    rows.forEach((r) => {
      const mfr = (r.manufacturer || "").trim() || "Other";
      const sizePart = hasSize(r.size) ? ` Size ${fmtSize(r.size)}` : "";
      const line = `• ${r.productName}${sizePart} – ${r.discount}% off`;
      if (!groups.has(mfr)) groups.set(mfr, []);
      groups.get(mfr).push(line);
    });

    const blocks = Array.from(groups.entries()).map(
      ([mfr, lines]) => `${mfr}\n${lines.join("\n")}`,
    );
    return "🏷️ Offer Items:\n\n" + blocks.join("\n\n") + "\n\n";
  };

  const noteBlock = customNote.trim() ? customNote.trim() + "\n\n" : "";
  const itemsBlock = buildItemsBlock();
  const previewMessage = MESSAGE_TEMPLATE
    .replace(/{customerName}/g, "[Customer Name]")
    .replace(/{shopName}/g, localStorage.getItem("companyName") || "Jaya Shree")
    .replace(/{offerTitle}/g, title.trim() || "<offer title>")
    .replace(/{itemsBlock}/g, itemsBlock)
    .replace(/{offerStartDate}/g, fmtDate(validFrom) || "<start>")
    .replace(/{validTill}/g, fmtDate(validTo) || "<end>")
    .replace(/{customNote}/g, noteBlock);

  // ── Item picker actions ──────────────────────────────────────────────────
  const openItemModal = () => setItemModalOpen(true);

  // Load the distinct filter options, narrowed by the current selections. This
  // is what makes the pickers data-driven: users choose from real values
  // instead of typing exact spellings.
  const loadItemFilterOptions = useCallback(async (name, type, manufacturer) => {
    try {
      const data = await offerService.getOfferItemFilters({
        name,
        medicineType: type,
        manufacturer,
      });
      setOptNames(Array.isArray(data?.productNames) ? data.productNames : []);
      setOptTypes(Array.isArray(data?.medicineTypes) ? data.medicineTypes : []);
      setOptManufacturers(Array.isArray(data?.manufacturers) ? data.manufacturers : []);
    } catch {
      // Non-fatal: pickers just show no suggestions. Search still works.
    }
  }, []);

  // Refresh options when the modal opens and whenever a selection changes, so
  // the type/manufacturer lists reflect what actually applies to the chosen
  // product (and vice-versa). Debounced lightly to coalesce rapid typing.
  useEffect(() => {
    if (!itemModalOpen) return;
    const t = setTimeout(() => {
      loadItemFilterOptions(fName, fType, fManufacturer);
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemModalOpen, fName, fType, fManufacturer]);

  // Fetch offer items from the backend using the current filters. All three
  // filters are optional and combine with AND server-side, so the browser only
  // ever holds the matching rows — this scales to large catalogs.
  const searchOfferItems = async () => {
    setItemsLoading(true);
    setItemsSearched(true);
    try {
      const data = await offerService.getOfferItems({
        name: fName,
        medicineType: fType,
        manufacturer: fManufacturer,
      });
      setItemsCatalog(Array.isArray(data) ? data : []);
    } catch {
      setItemsCatalog([]);
      showMsg("error", "Failed to load items");
    } finally {
      setItemsLoading(false);
    }
  };

  const clearItemFilters = () => {
    setFName("");
    setFType("");
    setFManufacturer("");
  };

  const itemKey = (it) => `${it.productName}|${it.size ?? ""}`;

  const toggleItem = (it) => {
    setSelectedItems((prev) => {
      const next = new Map(prev);
      const key = itemKey(it);
      if (next.has(key)) {
        next.delete(key);
      } else {
        const max = Number(it.maxDiscount) || 0;
        next.set(key, {
          productName: it.productName,
          manufacturer: it.manufacturer || "",
          size: it.size,
          maxDiscount: max,
          discount: max, // default to the max honorable discount
        });
      }
      return next;
    });
  };

  const setItemDiscount = (it, value) => {
    setSelectedItems((prev) => {
      const next = new Map(prev);
      const key = itemKey(it);
      const row = next.get(key);
      if (!row) return prev;
      const max = row.maxDiscount;
      let v = parseInt(value, 10);
      if (Number.isNaN(v) || v < 0) v = 0;
      if (v > max) v = max; // cap at the item's max honorable discount
      next.set(key, { ...row, discount: v });
      return next;
    });
  };

  // Remove a single selected item by its key ("productName|size"). Works from
  // the compose screen without reopening the picker or re-running the search,
  // since selection is keyed independently of the current filters.
  const removeSelectedItem = (key) => {
    setSelectedItems((prev) => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  };

  const clearSelectedItems = () => setSelectedItems(new Map());

  const validate = () => {
    const e = {};
    if (!title.trim()) e.title = "Title is required";
    if (validFrom && validFrom < todayStr) e.validFrom = "Valid From cannot be a past date";
    if (validTo && validTo < todayStr) e.validTo = "Valid Till cannot be a past date";
    if (validFrom && validTo && validTo < validFrom) e.validTo = "Valid Till cannot be before Valid From";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleReset = () => {
    setTitle("");
    setValidFrom("");
    setValidTo("");
    setCustomNote("");
    setSelectedItems(new Map());
    setFName("");
    setFType("");
    setFManufacturer("");
    setItemsCatalog([]);
    setItemsSearched(false);
    setErrors({});
    setFilterMobile("");
    setPageCustomers([]);
    setPage(0);
    setTotalPages(0);
    setTotalElements(0);
    setAudienceTotal(0);
    setLoaded(false);
    setSelectAll(false);
    setExcluded(new Set());
    setIncluded(new Map());
  };

  // Resolve the full recipient list. For "select all" we page through the
  // backend (server-side) so the browser never holds everything at once beyond
  // the batch being sent. For explicit selection we already have the mobiles.
  const resolveRecipients = async () => {
    if (!selectAll) {
      // Explicitly-picked customers carry their real name (Map: mobile -> name).
      return Array.from(included.entries()).map(([mobile, name]) => ({ mobile, name }));
    }
    // "Select All" means the FULL audience (matching what the X/total count
    // shows), so we page through unfiltered and drop the exclusions.
    const recipients = [];
    let p = 0;
    let pages = 1;
    do {
      const data = await orderService.searchCustomers({
        mobileNumber: null,
        page: p,
        size: 200,
      });
      pages = data.totalPages ?? 1;
      (data.content || []).forEach((c) => {
        if (!excluded.has(c.mobileNumber)) {
          recipients.push({ mobile: c.mobileNumber, name: c.customerName || "" });
        }
      });
      p += 1;
    } while (p < pages);
    return recipients;
  };

  const sendOffer = async () => {
    if (!validate()) {
      showMsg("error", "Please fix the errors before sending");
      return;
    }
    if (selectedCount === 0) {
      showMsg("error", "Select at least one customer");
      return;
    }
    if (channel === "whatsapp_api" && !waConnected) {
      showMsg("error", "Connect your WhatsApp and set a template before sending.");
      return;
    }

    setSending(true);
    setSendProgress(null);
    try {
      // Save the offer once (reusable + auditable).
      // Resolve the optional custom note + selected items now (collapse if
      // blank); the per-recipient placeholders stay for the backend to fill.
      const noteResolved = customNote.trim() ? customNote.trim() + "\n\n" : "";
      const templateToSave = MESSAGE_TEMPLATE
        .replace(/{itemsBlock}/g, itemsBlock)
        .replace(/{customNote}/g, noteResolved);
      const offer = await offerService.createOffer({
        title: title.trim(),
        messageTemplate: templateToSave,
        validFrom: validFrom || null,
        validTo: validTo || null,
      });

      const recipients = await resolveRecipients();
      if (recipients.length === 0) {
        showMsg("error", "No valid recipients to send");
        return;
      }

      const isWaMe = channel === "wa_me";

      // Free wa.me channel opens one browser tab per recipient (manual tap),
      // so warn/cap. Paid channels (SMS) send server-side — no tab limit.
      if (isWaMe && recipients.length > WA_ME_OPEN_LIMIT) {
        const proceed = window.confirm(
          `You selected ${recipients.length} customers. WhatsApp Link opens one ` +
          `chat per customer to tap send, so only the first ${WA_ME_OPEN_LIMIT} ` +
          `will open now (the rest are recorded). For large broadcasts, use the ` +
          `SMS channel.\n\nOpen the first ${WA_ME_OPEN_LIMIT}?`,
        );
        if (!proceed) return;
      }

      let openedTotal = 0;   // wa.me tabs opened
      let sentTotal = 0;     // server-side sends (SMS)
      let skippedTotal = 0;
      for (let i = 0; i < recipients.length; i += SEND_BATCH) {
        const batch = recipients.slice(i, i + SEND_BATCH);
        setSendProgress(`${Math.min(i + batch.length, recipients.length)}/${recipients.length}`);
        const result = await offerService.sendOffer({
          offerId: offer.id,
          recipients: batch,
        });
        const results = result.results || [];
        if (isWaMe) {
          const links = results.filter((r) => r.waLink).map((r) => r.waLink);
          for (const link of links) {
            if (openedTotal >= WA_ME_OPEN_LIMIT) break;
            window.open(link, "_blank");
            openedTotal += 1;
          }
        } else {
          // SMS: count backend-reported successful sends.
          sentTotal += results.filter((r) => r.status === "SENT").length;
        }
        skippedTotal += result.skipped || 0;
      }

      if (isWaMe) {
        const capped = recipients.length > WA_ME_OPEN_LIMIT;
        showMsg(
          "success",
          `Opened ${openedTotal} WhatsApp chat(s) — tap send in each.` +
            (capped ? ` ${recipients.length} recorded; open the rest in smaller batches.` : "") +
            (skippedTotal ? ` ${skippedTotal} skipped (invalid number).` : ""),
        );
      } else {
        const via = channel === "sms" ? "SMS" : "WhatsApp";
        showMsg(
          "success",
          `${via} sent to ${sentTotal} customer(s).` +
            (skippedTotal ? ` ${skippedTotal} skipped (invalid number).` : ""),
        );
      }
    } catch (err) {
      showMsg("error", err?.response?.data?.message || "Failed to send offer");
    } finally {
      setSending(false);
      setSendProgress(null);
    }
  };

  const busy = sending;

  return (
    <div className="h-full flex flex-col overflow-hidden bg-gradient-to-tr from-blue-50 via-indigo-50 to-teal-50 p-3 sm:p-4">
      <Header showSearch={false} showCart={false} />
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      {/* WhatsApp API not-connected banner */}
      {channel === "whatsapp_api" && !waConnected && (
        <div className="mt-3 flex items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <i className="fab fa-whatsapp text-green-600 text-lg" />
          <span className="flex-1">
            Connect your WhatsApp Business number and set an approved template before you can send offers.
          </span>
          <Link
            to="/offers/whatsapp-connect"
            className="whitespace-nowrap rounded-full bg-green-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-green-700"
          >
            Connect WhatsApp
          </Link>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-3 mt-3 flex-1 min-h-0">
        {/* LEFT: Select Customers panel */}
        <section className="w-full lg:w-96 flex-shrink-0 flex flex-col rounded-xl overflow-hidden shadow-md border border-blue-200 max-h-[60vh] lg:max-h-none">
          <div className="px-3 py-2 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-blue-800">👥 Select Customers</h2>
              <span className="text-xs font-semibold text-blue-700">
                {selectedCount}/{audienceTotal} selected
              </span>
            </div>
            {/* Partial (prefix) mobile filter — server-side, live as you type */}
            <div className="mt-2 flex items-center gap-2">
              <input
                value={filterMobile}
                onChange={(e) => setFilterMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                onKeyDown={(e) => { if (e.key === "Enter") loadCustomers(); }}
                maxLength={10}
                placeholder="Filter by mobile (starts with)"
                className="flex-1 px-2 py-1 border border-blue-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 focus:outline-none"
              />
              <button
                onClick={loadCustomers}
                disabled={custLoading}
                className={`flex items-center gap-1 px-3 py-1 rounded-full text-white font-semibold text-xs transition ${custLoading ? "bg-gray-400" : "bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-indigo-600 hover:to-blue-600"}`}
              >
                {custLoading ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-sync" />}
                Load
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-auto min-h-0">
            <table className="min-w-full text-xs text-gray-800">
              <thead className="bg-gradient-to-r from-blue-100 via-indigo-50 to-teal-50 text-blue-900 uppercase tracking-wide sticky top-0">
                <tr>
                  <th className="py-2 px-3 text-center w-14">
                    <div className="flex flex-col items-center gap-0.5">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={toggleSelectAll}
                        disabled={!loaded || totalElements === 0}
                        className="h-4 w-4 accent-indigo-500"
                        title="Select all matching customers"
                      />
                      <span className="text-[10px] normal-case">All</span>
                    </div>
                  </th>
                  <th className="py-2 px-3 text-left">Customer</th>
                  <th className="py-2 px-3 text-left">Mobile</th>
                </tr>
              </thead>
              <tbody>
                {custLoading ? (
                  <tr><td colSpan={3} className="text-center py-6"><i className="fas fa-spinner fa-spin text-blue-400 text-2xl" /></td></tr>
                ) : !loaded ? (
                  <tr><td colSpan={3} className="py-8 text-center text-gray-400">
                    <i className="fas fa-users text-3xl mb-2 block" />
                    <p className="text-xs">Click "Load" to fetch customers.</p>
                  </td></tr>
                ) : pageCustomers.length === 0 ? (
                  <tr><td colSpan={3} className="py-8 text-center text-gray-400">
                    <i className="fas fa-inbox text-3xl mb-2 block" />
                    <p className="text-xs">No customers found.</p>
                  </td></tr>
                ) : (
                  pageCustomers.map((c, idx) => (
                    <tr key={c.mobileNumber} className={`border-t ${idx % 2 === 0 ? "bg-white" : "bg-blue-50"} hover:bg-indigo-50`}>
                      <td className="py-1 px-3 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected(c.mobileNumber)}
                          onChange={() => toggleOne(c)}
                          className="h-4 w-4 accent-indigo-500"
                        />
                      </td>
                      <td className="py-1 px-3">{c.customerName || "—"}</td>
                      <td className="py-1 px-3 font-mono">{c.mobileNumber}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Server-side pagination — 15 per page */}
          {loaded && totalPages > 1 && (
            <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100 bg-white">
              <span className="text-[11px] text-gray-500">
                {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalElements)} of {totalElements}
              </span>
              <div className="flex items-center gap-1">
                <button onClick={() => fetchPage(page - 1, filterMobile)} disabled={page === 0 || custLoading}
                  className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
                  <i className="fas fa-chevron-left" />
                </button>
                <span className="text-[11px] text-gray-600">{page + 1}/{totalPages}</span>
                <button onClick={() => fetchPage(page + 1, filterMobile)} disabled={page >= totalPages - 1 || custLoading}
                  className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
                  <i className="fas fa-chevron-right" />
                </button>
              </div>
            </div>
          )}
        </section>

        {/* RIGHT: Offer composer */}
        <section className="flex-1 min-h-0 flex flex-col rounded-xl overflow-hidden shadow-md border border-blue-200">
          <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200">
            <h2 className="text-base font-bold text-blue-800">📢 Create Offer</h2>
            <button
              type="button"
              onClick={openItemModal}
              className="flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-800 hover:underline"
            >
              <i className="fas fa-tags" /> Select Items
              {selectedItems.size > 0 && (
                <span className="ml-1 rounded-full bg-indigo-600 px-1.5 text-xs text-white">
                  {selectedItems.size}
                </span>
              )}
            </button>
          </div>
          <div className="p-4 overflow-auto flex-1 min-h-0">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Offer Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => { setTitle(e.target.value); if (errors.title) setErrors((p) => ({ ...p, title: "" })); }}
                  maxLength={150}
                  placeholder="e.g. Diwali 20% Off"
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none ${errors.title ? "border-red-400 bg-red-50" : "border-blue-300"}`}
                />
                {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Valid From</label>
                <input
                  type="date"
                  value={validFrom}
                  min={todayStr}
                  onChange={(e) => { setValidFrom(e.target.value); if (errors.validFrom) setErrors((p) => ({ ...p, validFrom: "" })); }}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none ${errors.validFrom ? "border-red-400 bg-red-50" : "border-blue-300"}`}
                />
                {errors.validFrom && <p className="text-red-500 text-xs mt-1">{errors.validFrom}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Valid Till</label>
                <input
                  type="date"
                  value={validTo}
                  min={validFrom || todayStr}
                  onChange={(e) => { setValidTo(e.target.value); if (errors.validTo) setErrors((p) => ({ ...p, validTo: "" })); }}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none ${errors.validTo ? "border-red-400 bg-red-50" : "border-blue-300"}`}
                />
                {errors.validTo && <p className="text-red-500 text-xs mt-1">{errors.validTo}</p>}
              </div>
              <div className="sm:col-span-3">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Additional Message <span className="text-gray-400">(optional)</span>
                </label>
                <textarea
                  value={customNote}
                  onChange={(e) => setCustomNote(e.target.value)}
                  rows={2}
                  maxLength={500}
                  placeholder="e.g. Use code SAVE10 at checkout. Open till 9 PM."
                  className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Added customize message if you want or leave blank to skip.
                </p>
              </div>

              <div className="sm:col-span-3">
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-gray-600">
                    Message Preview
                  </label>
                  {selectedItems.size > 0 && (
                    <button
                      type="button"
                      onClick={() => setRemoveModalOpen(true)}
                      className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 hover:underline"
                    >
                      <i className="fas fa-list-ul mr-1" />Remove items ({selectedItems.size})
                    </button>
                  )}
                </div>
                <div className="w-full px-3 py-3 border border-blue-200 rounded-lg text-sm bg-blue-50/50 text-gray-800 whitespace-pre-wrap font-sans">
                  {previewMessage}
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  The format is fixed; each customer's name is inserted automatically
                  (their mobile number if no name is on file).
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-100 bg-white">
            {sendProgress && <span className="text-xs text-gray-500 mr-auto">Sending {sendProgress}…</span>}
            <button
              type="button"
              onClick={handleReset}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 font-semibold text-xs transition"
            >
              <i className="fas fa-redo" /> Reset
            </button>
            <button
              onClick={sendOffer}
              disabled={busy || selectedCount === 0 || (channel === "whatsapp_api" && !waConnected)}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-white font-semibold shadow text-xs transition ${busy || selectedCount === 0 || (channel === "whatsapp_api" && !waConnected) ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-emerald-600 hover:to-green-700"}`}
            >
              {busy
                ? <><i className="fas fa-spinner fa-spin" />Sending...</>
                : channel === "sms"
                  ? <><i className="fas fa-sms" />Send via SMS ({selectedCount})</>
                  : channel === "whatsapp_api"
                    ? <><i className="fab fa-whatsapp" />Send via WhatsApp API ({selectedCount})</>
                    : <><i className="fab fa-whatsapp" />Send via WhatsApp ({selectedCount})</>}
            </button>
          </div>
        </section>
      </div>

      {/* Item picker modal */}
      {itemModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-3xl h-[90vh] flex flex-col rounded-xl bg-white shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200">
              <h3 className="text-sm font-bold text-blue-800">🏷️ Select Offer Items</h3>
              <button onClick={() => setItemModalOpen(false)} className="text-gray-500 hover:text-gray-700">
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="p-3 border-b border-gray-100 space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {/* Product name — type-ahead backed by real catalog values */}
                <div>
                  <input
                    list="offer-name-options"
                    value={fName}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFName(v);
                      // Clearing the product name resets the dependent filters,
                      // so type/manufacturer never stay pinned to a product the
                      // user just removed.
                      if (!v.trim()) {
                        setFType("");
                        setFManufacturer("");
                      }
                    }}
                    onKeyDown={(e) => { if (e.key === "Enter") searchOfferItems(); }}
                    placeholder="Product name (type to search)"
                    className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                  />
                  <datalist id="offer-name-options">
                    {optNames.map((n) => <option key={n} value={n} />)}
                  </datalist>
                </div>
                {/* Product type — dropdown of values that apply to the selection */}
                <select
                  value={fType}
                  onChange={(e) => setFType(e.target.value)}
                  className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-400 focus:outline-none"
                >
                  <option value="">All product types</option>
                  {optTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                {/* Manufacturer — dropdown of values that apply to the selection */}
                <select
                  value={fManufacturer}
                  onChange={(e) => setFManufacturer(e.target.value)}
                  className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-400 focus:outline-none"
                >
                  <option value="">All manufacturers</option>
                  {optManufacturers.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={clearItemFilters}
                  className="px-3 py-1.5 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 font-semibold text-xs"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={searchOfferItems}
                  disabled={itemsLoading}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-white font-semibold text-xs transition ${itemsLoading ? "bg-gray-400" : "bg-gradient-to-r from-indigo-500 to-blue-600 hover:from-indigo-600 hover:to-blue-700"}`}
                >
                  {itemsLoading ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-search" />}
                  Search
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-auto">
              {itemsLoading ? (
                <div className="py-8 text-center"><i className="fas fa-spinner fa-spin text-blue-400 text-2xl" /></div>
              ) : itemsCatalog.length === 0 ? (
                <div className="py-8 text-center text-gray-400 text-sm">
                  {itemsSearched ? "No items match your filters." : "Set filters and click Search to find items."}
                </div>
              ) : (
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 text-xs uppercase sticky top-0">
                    <tr>
                      <th className="py-2 px-3 text-center w-10"></th>
                      <th className="py-2 px-3 text-left">Product</th>
                      <th className="py-2 px-3 text-left">Manufacturer</th>
                      <th className="py-2 px-3 text-center">Size</th>
                      <th className="py-2 px-3 text-center">Max %</th>
                      <th className="py-2 px-3 text-center">Discount %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemsCatalog.map((it, idx) => {
                      const key = itemKey(it);
                      const sel = selectedItems.get(key);
                      const checked = Boolean(sel);
                      const max = Number(it.maxDiscount) || 0;
                      return (
                        <tr key={key + idx} className={`border-t ${idx % 2 ? "bg-gray-50" : "bg-white"}`}>
                          <td className="py-1 px-3 text-center">
                            <input type="checkbox" checked={checked} onChange={() => toggleItem(it)}
                              className="h-4 w-4 accent-indigo-500" />
                          </td>
                          <td className="py-1 px-3">
                            {it.productName}
                            {it.medicineType ? <span className="ml-1 text-[10px] text-gray-400">({it.medicineType})</span> : null}
                          </td>
                          <td className="py-1 px-3 text-gray-600">{it.manufacturer || "—"}</td>
                          <td className="py-1 px-3 text-center">{hasSize(it.size) ? fmtSize(it.size) : "—"}</td>
                          <td className="py-1 px-3 text-center text-gray-500">{max}%</td>
                          <td className="py-1 px-3 text-center">
                            <input
                              type="number"
                              min={0}
                              max={max}
                              disabled={!checked}
                              value={checked ? sel.discount : ""}
                              onChange={(e) => setItemDiscount(it, e.target.value)}
                              className="w-16 px-2 py-1 border border-blue-300 rounded text-xs text-center disabled:bg-gray-100"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-white">
              <span className="text-xs text-gray-500">{selectedItems.size} selected</span>
              <button onClick={() => setItemModalOpen(false)}
                className="px-4 py-1.5 rounded-full bg-gradient-to-r from-indigo-500 to-blue-600 text-white text-xs font-semibold hover:from-indigo-600 hover:to-blue-700">
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove-items popup — compact list of selected items, each removable */}
      {removeModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg h-[90vh] flex flex-col rounded-xl bg-white shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200">
              <h3 className="text-sm font-bold text-blue-800">🗑️ Remove Offer Items</h3>
              <button onClick={() => setRemoveModalOpen(false)} className="text-gray-500 hover:text-gray-700">
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="flex-1 overflow-auto">
              {selectedItems.size === 0 ? (
                <div className="py-8 text-center text-gray-400 text-sm">No items selected.</div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {Array.from(selectedItems.entries()).map(([key, r]) => {
                    const sizePart = hasSize(r.size) ? ` · Size ${fmtSize(r.size)}` : "";
                    return (
                      <li key={key} className="flex items-center justify-between px-4 py-2 text-sm hover:bg-gray-50">
                        <span className="text-gray-800">
                          {r.productName}{sizePart} <span className="text-gray-500">– {r.discount}% off</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => removeSelectedItem(key)}
                          title="Remove this item"
                          className="ml-3 flex-shrink-0 rounded-full px-2 py-1 text-red-500 hover:bg-red-50 hover:text-red-700"
                        >
                          <i className="fas fa-times" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-white">
              <button
                type="button"
                onClick={clearSelectedItems}
                disabled={selectedItems.size === 0}
                className="text-xs font-semibold text-red-500 hover:text-red-700 hover:underline disabled:opacity-40"
              >
                Clear all
              </button>
              <button onClick={() => setRemoveModalOpen(false)}
                className="px-4 py-1.5 rounded-full bg-gradient-to-r from-indigo-500 to-blue-600 text-white text-xs font-semibold hover:from-indigo-600 hover:to-blue-700">
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfferCreate;
